import React, { useMemo } from 'react';
import { Chip, Stack } from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { alpha } from '@mui/material/styles';
import { format, parseISO, isValid } from 'date-fns';
import { useResources } from '@platform/core/src/context/ResourceContext';

const formatDateTime = (isoString) => {
  if (!isoString) return '';
  const date = parseISO(isoString);
  if (!isValid(date)) return '';
  return format(date, 'MMM d, h:mm a');
};

const CalendarEffectRenderer = ({ event }) => {
  const { patches, change_type, value, resource_id } = event;
  
  // Hook into the global resource state to get the full object details
  // (Title, start time, etc.) that were fetched by the sync driver.
  const { getResourceById } = useResources();
  const resource = getResourceById(resource_id);

  const changesToRender = useMemo(() => {
    const changes = [];

    // 1. Handle Creation
    if (change_type === 'created') {
      // Prefer the title from the loaded resource, fallback to event value, then generic
      const title = resource?.title || value?.title || 'Event';
      
      // Prefer time from resource, fallback to event value
      const startTime = resource?.start_datetime || value?.start_datetime;
      const timeStr = formatDateTime(startTime);
      
      changes.push({
        id: `created-${resource_id}`,
        label: `Scheduled: ${title} ${timeStr ? `for ${timeStr}` : ''}`,
        icon: <EventIcon fontSize="small" />,
        color: "primary"
      });
      return changes;
    }

    // 2. Handle Deletion
    if (change_type === 'deleted') {
      // Even if deleted, we might still have the old title in memory/context
      const title = resource?.title || value?.title || 'Event';
      
      changes.push({
        id: `deleted-${resource_id}`,
        label: `Removed "${title}" from schedule`,
        icon: <DeleteIcon fontSize="small" />,
        color: "error"
      });
      return changes;
    }

    // 3. Handle Updates (Patches)
    if (patches && Array.isArray(patches)) {
      let timeChanged = false;
      let titleChanged = false;

      patches.forEach((patch) => {
        if (patch.path.includes('start_datetime') || patch.path.includes('end_datetime')) {
          timeChanged = true;
        }
        if (patch.path.includes('title')) {
          titleChanged = true;
        }
      });

      if (timeChanged) {
        const newTime = resource?.start_datetime;
        const timeLabel = newTime ? ` to ${formatDateTime(newTime)}` : '';
        
        changes.push({
          id: `rescheduled-${resource_id}`,
          label: `Rescheduled${timeLabel}`,
          icon: <EditIcon fontSize="small" />,
          color: "info"
        });
      } 
      
      if (titleChanged) {
        const newTitle = resource?.title || 'Event';
        changes.push({
          id: `renamed-${resource_id}`,
          label: `Renamed to "${newTitle}"`,
          icon: <EditIcon fontSize="small" />,
          color: "info"
        });
      }
    }

    return changes;
  }, [change_type, value, patches, resource_id, resource]);

  if (changesToRender.length === 0) return null;

  return (
    <Stack direction="column" spacing={0.5} sx={{ width: '100%', my: 0.5 }}>
      {changesToRender.map((change) => (
        <Chip
          key={change.id}
          icon={change.icon}
          label={change.label}
          size="small"
          variant="outlined"
          color={change.color || "default"}
          sx={{
            maxWidth: '100%',
            justifyContent: 'flex-start',
            bgcolor: (theme) => alpha(theme.palette[change.color || 'primary'].main, 0.05),
            borderColor: (theme) => alpha(theme.palette[change.color || 'primary'].main, 0.3),
            '& .MuiChip-label': {
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              fontWeight: 500
            }
          }}
        />
      ))}
    </Stack>
  );
};

export default CalendarEffectRenderer;